// Phase 1B.10.2 — Scoreline grid + 1X2 marginalization.
//
// Pure functions. Allowed to use poissonPmf from ./poisson.ts.
// Math.exp / factorial are NOT in this file directly; they live in
// ./poisson.ts behind the poissonPmf API.
//
// Grid: 0..MAX_GOALS × 0..MAX_GOALS = 121 cells when MAX_GOALS=10.
// Each cell P(h, a) = poissonPmf(homeλ, h) * poissonPmf(awayλ, a)
// (independence assumption — Layer 1 simplification).
//
// Normalization: divide by grid mass so home_win + draw + away_win
// sum to exactly 1 (modulo floating-point). Tail probability =
// 1 - grid mass; recorded for diagnostic and for reject-when-large.

import { poissonPmf } from "./poisson.js";

export const SCORELINE_GRID_MAX_GOALS = 10;
export const SCORELINE_TAIL_REJECT_THRESHOLD = 0.005;
export const SCORELINE_TOP_N = 10;

export interface ScorelineCell {
  home: number;
  away: number;
  probability: number; // raw (pre-normalization)
}

export interface ScorelineGridResult {
  cells: ScorelineCell[]; // length (max_goals+1)^2, raw probabilities
  grid_mass: number; // sum of raw probabilities
  tail_probability: number; // 1 - grid_mass
  max_goals: number;
}

export function buildScorelineGrid(
  homeLambda: number,
  awayLambda: number,
  maxGoals: number = SCORELINE_GRID_MAX_GOALS,
): ScorelineGridResult {
  const cells: ScorelineCell[] = [];
  let mass = 0;

  // Cache per-side PMFs to avoid (max+1)^2 redundant computations.
  const homePmf: number[] = [];
  const awayPmf: number[] = [];
  for (let k = 0; k <= maxGoals; k++) {
    homePmf.push(poissonPmf(homeLambda, k));
    awayPmf.push(poissonPmf(awayLambda, k));
  }

  for (let h = 0; h <= maxGoals; h++) {
    for (let a = 0; a <= maxGoals; a++) {
      const p = homePmf[h]! * awayPmf[a]!;
      cells.push({ home: h, away: a, probability: p });
      mass += p;
    }
  }
  const tail = Math.max(0, 1 - mass);
  return {
    cells,
    grid_mass: mass,
    tail_probability: tail,
    max_goals: maxGoals,
  };
}

export interface OneXTwoMarginals {
  home_win_probability: number; // normalized
  draw_probability: number; // normalized
  away_win_probability: number; // normalized
  tail_probability: number; // raw 1 - grid_mass
}

export function marginalize1X2(grid: ScorelineGridResult): OneXTwoMarginals {
  let homeWin = 0;
  let draw = 0;
  let awayWin = 0;
  for (const c of grid.cells) {
    if (c.home > c.away) homeWin += c.probability;
    else if (c.home === c.away) draw += c.probability;
    else awayWin += c.probability;
  }
  // Normalize by grid mass so the three probabilities sum to 1.
  // grid_mass is always > 0 in any reachable parameter range
  // (poissonPmf at k=0 with finite λ is non-zero).
  const mass = grid.grid_mass;
  if (mass <= 0) {
    throw new Error("marginalize1X2: grid_mass must be > 0");
  }
  return {
    home_win_probability: homeWin / mass,
    draw_probability: draw / mass,
    away_win_probability: awayWin / mass,
    tail_probability: grid.tail_probability,
  };
}

export interface TopScoreline {
  home: number;
  away: number;
  probability: number; // normalized
}

export function topScorelines(
  grid: ScorelineGridResult,
  n: number = SCORELINE_TOP_N,
): TopScoreline[] {
  const mass = grid.grid_mass;
  if (mass <= 0) return [];
  // Sort a copy by raw probability desc.
  const sorted = [...grid.cells].sort((a, b) => b.probability - a.probability);
  return sorted.slice(0, n).map((c) => ({
    home: c.home,
    away: c.away,
    probability: c.probability / mass,
  }));
}
